import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown, Search, Filter } from 'lucide-react';
export interface Column<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  width?: string;
  minWidth?: string;
}

export interface DataGridProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  searchable?: boolean;
  groupBy?: keyof T;
  className?: string;
  /** fixed = equal column squeeze; auto = respect min widths with horizontal scroll */
  tableLayout?: 'fixed' | 'auto';
  /** Renders in the first header cell when the first column has an empty header (e.g. select-all checkbox). */
  leadingHeader?: React.ReactNode;
}

export function DataGrid<T extends Record<string, any>>({
  data,
  columns,
  onRowClick,
  searchable = false,
  groupBy,
  className = '',
  tableLayout = 'fixed',
  leadingHeader,
}: DataGridProps<T>) {
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  // Filtering logic
  const filteredData = useMemo(() => {
    let filtered = sortedData;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Column filters
    Object.entries(filters).forEach(([key, filterValue]) => {
      if (filterValue) {
        filtered = filtered.filter(row =>
          String(row[key]).toLowerCase().includes(filterValue.toLowerCase())
        );
      }
    });

    return filtered;
  }, [sortedData, searchTerm, filters]);

  // Grouping logic
  const groupedData = useMemo(() => {
    if (!groupBy) return { ungrouped: filteredData };

    return filteredData.reduce((groups, row) => {
      const key = String(row[groupBy]);
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
      return groups;
    }, {} as Record<string, T[]>);
  }, [filteredData, groupBy]);

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return current.direction === 'asc'
          ? { key, direction: 'desc' }
          : null;
      }
      return { key, direction: 'asc' };
    });
  };

  const renderCell = (column: Column<T>, row: T) => {
    const value = row[column.key as keyof T];
    return column.render ? column.render(value, row) : String(value);
  };

  return (
    <div className={`bg-khiimori-bg-elevated rounded-lg shadow-sm border border-khiimori-border overflow-hidden ${className}`}>
      {/* Search & Filters */}
      {searchable && (
        <div className="p-4 border-b border-khiimori-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-khiimori-text-muted" />
            <input
              type="text"
              placeholder="Search across all columns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-khiimori-bg-elevated text-khiimori-text-primary text-khiimori-text-primary
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className={tableLayout === 'auto' ? 'w-full min-w-[960px] table-auto' : 'w-full table-fixed'}>
          <thead className="bg-khiimori-bg-secondary  border-b border-khiimori-border">
            <tr>
              {columns.map((column, idx) => (
                <th
                  key={idx}
                  style={{ width: column.width, minWidth: column.minWidth ?? column.width }}
                  className={`px-4 py-3 text-left text-xs font-semibold text-khiimori-text-secondary uppercase tracking-wider
                            ${column.sortable ? 'cursor-pointer hover:bg-khiimori-bg-elevated transition-colors' : ''}`}
                  onClick={() => column.sortable && handleSort(column.key as string)}
                >
                  <div className="flex items-center gap-2">
                    {idx === 0 && leadingHeader ? leadingHeader : null}
                    {column.header ? <span>{column.header}</span> : null}
                    {column.sortable && (
                      <div className="flex flex-col">
                        <ChevronUp
                          className={`w-3 h-3 transition-colors ${
                            sortConfig?.key === column.key && sortConfig.direction === 'asc'
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-khiimori-text-muted'
                          }`}
                        />
                        <ChevronDown
                          className={`w-3 h-3 -mt-1 transition-colors ${
                            sortConfig?.key === column.key && sortConfig.direction === 'desc'
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-khiimori-text-muted'
                          }`}
                        />
                      </div>
                    )}
                    {column.filterable && (
                      <Filter className="w-3 h-3 text-khiimori-text-muted hover:text-blue-600 transition-colors" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            <AnimatePresence mode="popLayout">
              {Object.entries(groupedData).map(([groupKey, rows]) => (
                <React.Fragment key={groupKey}>
                  {groupBy && groupKey !== 'ungrouped' && (
                    <tr className="bg-khiimori-bg-secondary ">
                      <td colSpan={columns.length} className="px-6 py-3 text-sm font-semibold text-khiimori-text-secondary">
                        {groupKey} ({rows.length})
                      </td>
                    </tr>
                  )}
                  {rows.map((row, rowIdx) => {
                    const rowKey =
                      typeof row.id === 'string' || typeof row.id === 'number'
                        ? String(row.id)
                        : rowIdx;
                    return (
                    <motion.tr
                      key={rowKey}
                      initial={false}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2, delay: rowIdx * 0.02 }}
                      onClick={() => onRowClick?.(row)}
                      className={`hover:bg-khiimori-bg-secondary hover:bg-khiimori-bg-elevated transition-colors
                                ${onRowClick ? 'cursor-pointer' : ''}`}
                    >
                      {columns.map((column, colIdx) => (
                        <td
                          key={colIdx}
                          style={{ width: column.width, minWidth: column.minWidth ?? column.width }}
                          className="px-4 py-3 text-sm text-khiimori-text-primary align-middle min-w-0"
                        >
                          {renderCell(column, row)}
                        </td>
                      ))}
                    </motion.tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-khiimori-bg-secondary  border-t border-khiimori-border">
        <p className="text-sm text-khiimori-text-secondary">
          Showing <span className="font-semibold">{filteredData.length}</span> of{' '}
          <span className="font-semibold">{data.length}</span> results
        </p>
      </div>
    </div>
  );
}
